import React, { useMemo, useRef, useState } from 'react';
import { ActivityIndicator, Pressable, StyleSheet, View } from 'react-native';
import { useTranslation } from 'react-i18next';

import {
  defaultProfile,
  playableProfiles,
  type StreamCamera,
} from '@/entities/stream/types';
import { isConnecting, useAutoLive } from '../lib/use-auto-live';
import {
  CAN_SNAPSHOT,
  CAN_ZOOM,
  saveSnapshot,
  useDigitalZoom,
  useDragSource,
  useDropTarget,
  useHoverWithin,
  type DragPayload,
} from '../lib/tile-dom';
import { VideoSurface } from './VideoSurface';
import { Icon, type IconName } from '@/shared/ui/icons';
import { formatTimestamp } from '@/shared/lib/format';
import { fonts } from '@/shared/ui/fonts';
import { AppText } from '@/shared/ui/text';
import { spacing, useTheme } from '@/shared/ui/theme';

/**
 * Одно окно раскладки — как окно в живом просмотре регистратора.
 *
 * Чёрное при любой теме: это экран, а не карточка, и картинка с камеры на светлом
 * поле читается хуже, чем на тёмном. Подписи — поверх картинки, на полупрозрачной
 * плашке, чтобы окно отдавало видео всю свою площадь.
 *
 *   нажатие         — выбрать окно (в него откроется следующая камера из дерева);
 *   двойное нажатие — развернуть на всю раскладку и обратно;
 *   перетаскивание  — камера из дерева в окно, окно на окно (меняются местами);
 *   колесо мыши     — цифровой зум к точке под курсором, тяга — двигать картинку;
 *   SD / HD         — дополнительный поток (по умолчанию) или основной;
 *   снимок, ✕       — кадр в PNG, закрыть камеру в окне.
 *
 * Кнопки видны под курсором и у выбранного окна: на телефоне курсора нет, и
 * выбранное окно — то, с которым сейчас работают. Под курсором же внизу — что
 * приходит на самом деле: разрешение и частота кадров.
 */
export const VIDEO_BG = '#000000';
/** Линии между окнами — тёмно-серые, как в регистраторе. */
export const GRID_LINE = '#1f2023';
const SCRIM = 'rgba(0,0,0,0.6)';
const ON_VIDEO = '#ffffff';
const ON_VIDEO_MUTED = 'rgba(255,255,255,0.6)';
const ON_VIDEO_FAINT = 'rgba(255,255,255,0.22)';
const DROP_WASH = 'rgba(255,255,255,0.08)';

/** Два нажатия ближе этого — двойное. */
const DOUBLE_PRESS_MS = 300;

const fill = {
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
} as const;

const styles = StyleSheet.create({
  root: { flex: 1, backgroundColor: VIDEO_BG, overflow: 'hidden' },
  frame: { ...fill, borderWidth: 1.5 },
  center: {
    ...fill,
    alignItems: 'center',
    justifyContent: 'center',
    gap: spacing(2),
    padding: spacing(2),
    paddingTop: 28,
  },
  centerText: { textAlign: 'center' },
  // Подпись и кнопки — плашками у краёв, а не полосой во всю ширину: картинке
  // остаётся всё, кроме двух уголков.
  head: {
    position: 'absolute',
    top: spacing(1),
    left: spacing(1),
    right: spacing(1),
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing(1),
  },
  nameChip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    height: 22,
    paddingHorizontal: 7,
    borderRadius: 3,
    backgroundColor: SCRIM,
    maxWidth: '70%',
  },
  name: { flexShrink: 1 },
  headSpacer: { flex: 1 },
  tools: {
    flexDirection: 'row',
    alignItems: 'center',
    height: 24,
    borderRadius: 3,
    backgroundColor: SCRIM,
    paddingHorizontal: 1,
  },
  foot: {
    position: 'absolute',
    right: spacing(1),
    bottom: spacing(1),
    height: 20,
    paddingHorizontal: 7,
    borderRadius: 3,
    justifyContent: 'center',
    backgroundColor: SCRIM,
  },
  chip: {
    paddingHorizontal: 6,
    height: 22,
    alignItems: 'center',
    justifyContent: 'center',
  },
  tool: {
    width: 24,
    height: 24,
    borderRadius: 3,
    alignItems: 'center',
    justifyContent: 'center',
  },
  toolHot: { backgroundColor: 'rgba(255,255,255,0.14)' },
  index: { position: 'absolute', left: spacing(2), bottom: spacing(1) },
  dot: { width: 6, height: 6, borderRadius: 3 },
  mono: { fontFamily: fonts.mono.regular },
  drop: { backgroundColor: DROP_WASH },
  scrim: { backgroundColor: SCRIM },
});

function isHot(state: { pressed: boolean }): boolean {
  return state.pressed || (state as { hovered?: boolean }).hovered === true;
}

/** Подсказка под курсором: на вебе — атрибут `title`, на телефоне его нет. */
function tooltip(label: string): object {
  return { title: label };
}

/** Кнопка на плашке поверх видео. */
function Tool({
  icon,
  label,
  onPress,
  testID,
}: {
  icon: IconName;
  label: string;
  onPress: () => void;
  testID?: string;
}) {
  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={label}
      {...tooltip(label)}
      onPress={onPress}
      style={state => [styles.tool, isHot(state) && styles.toolHot]}
      testID={testID}
    >
      <Icon name={icon} size={14} color={ON_VIDEO} />
    </Pressable>
  );
}

export function LiveTile({
  companyId,
  camera,
  index,
  selected,
  onPress,
  onDoublePress,
  onClose,
  onDrop,
  lastSeen,
  testID,
}: {
  companyId: string;
  /** `null` — пустое окно. */
  camera: StreamCamera | null;
  /** Когда источник камеры последний раз был на связи (строка устройств). */
  lastSeen?: string | null;
  /** Номер окна, с нуля; подписывается с единицы, как в регистраторе. */
  index: number;
  selected: boolean;
  onPress: () => void;
  onDoublePress: () => void;
  onClose: () => void;
  /** В окно бросили камеру из дерева или другое окно. */
  onDrop: (payload: DragPayload) => void;
  testID?: string;
}) {
  const { t } = useTranslation();
  const p = useTheme();
  const ref = useRef<View>(null);
  const lastPress = useRef(0);
  const hot = useHoverWithin(ref);
  // Окно ниже этого — шестая часть телефона или клетка «16»: там помещается одна
  // строка причины, а значок над ней только сталкивает её за край.
  const [compact, setCompact] = useState(false);
  const zoom = useDigitalZoom(ref, CAN_ZOOM && !!camera);
  // Под зумом тяга мышью двигает картинку, а не окно.
  useDragSource(
    ref,
    camera && zoom.scale === 1 ? { kind: 'window', index } : null,
  );
  const over = useDropTarget(ref, onDrop);

  const press = () => {
    const now = Date.now();
    if (now - lastPress.current < DOUBLE_PRESS_MS) {
      lastPress.current = 0;
      onDoublePress();
      return;
    }
    lastPress.current = now;
    onPress();
  };

  return (
    <Pressable
      ref={ref}
      testID={testID}
      // Без роли кнопки: на вебе она стала бы <button>, а внутри окна — свои кнопки
      // (SD/HD, снимок, ✕), и вложенная кнопка — это невалидный HTML.
      accessibilityLabel={
        camera ? camera.name || camera.deviceId : t('live.view.emptyWindow')
      }
      accessibilityState={{ selected }}
      onPress={press}
      onLayout={e => setCompact(e.nativeEvent.layout.height < 150)}
      style={[styles.root, over && styles.drop]}
    >
      {camera ? (
        <TileVideo
          // Ключ — камера: другая камера в том же окне — новая сессия, старая закрыта.
          key={camera.deviceId}
          companyId={companyId}
          camera={camera}
          controls={hot || selected}
          hot={hot}
          zoom={zoom.scale}
          onResetZoom={zoom.reset}
          onSnapshot={() => saveSnapshot(ref.current, snapshotName(camera))}
          onClose={onClose}
          lastSeen={lastSeen}
          // Подсказка «что делать» — под курсором и у выбранного окна: на стене из
          // шестнадцати окон шестнадцать абзацев читаются как одна большая ошибка.
          showHint={(hot || selected) && !compact}
          compact={compact}
        />
      ) : (
        <View style={styles.center} pointerEvents="none">
          <Icon
            name="camera"
            size={22}
            color={selected || hot ? ON_VIDEO_MUTED : ON_VIDEO_FAINT}
          />
          {selected || hot ? (
            <AppText
              variant="micro"
              color={ON_VIDEO_MUTED}
              style={styles.centerText}
            >
              {t('live.view.pickHint')}
            </AppText>
          ) : null}
        </View>
      )}
      {camera ? null : (
        <View style={styles.index} pointerEvents="none">
          <AppText variant="micro" color={ON_VIDEO_FAINT} style={styles.mono}>
            {index + 1}
          </AppText>
        </View>
      )}
      <View
        pointerEvents="none"
        style={[
          styles.frame,
          {
            borderColor: over ? ON_VIDEO : selected ? p.primary : 'transparent',
          },
        ]}
      />
    </Pressable>
  );
}

/** `Вход_2026-10-22_07-22-19.png` — имя, по которому снимок находят в загрузках. */
function snapshotName(camera: StreamCamera): string {
  const now = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  const stamp = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(
    now.getDate(),
  )}_${pad(now.getHours())}-${pad(now.getMinutes())}-${pad(now.getSeconds())}`;
  const name = (camera.name || camera.deviceId).replace(/[\\/:*?"<>|]+/g, '_');
  return `${name}_${stamp}.png`;
}

function TileVideo({
  companyId,
  camera,
  controls,
  hot,
  zoom,
  onResetZoom,
  onSnapshot,
  onClose,
  lastSeen,
  showHint,
  compact,
}: {
  companyId: string;
  camera: StreamCamera;
  lastSeen?: string | null;
  showHint: boolean;
  compact: boolean;
  controls: boolean;
  hot: boolean;
  zoom: number;
  onResetZoom: () => void;
  onSnapshot: () => void;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const p = useTheme();
  const playable = useMemo(() => playableProfiles(camera), [camera]);
  // Дополнительный поток по умолчанию — шестнадцать окон основного потока кладут
  // канал объекта. Основной — осознанный выбор для одного окна.
  const sub = defaultProfile(camera)?.token ?? '';
  const main = playable[0]?.token ?? '';
  const [profile, setProfile] = useState(sub);
  const { state, restart } = useAutoLive({
    companyId,
    camera,
    profile: profile || null,
  });

  const live = state.phase === 'playing' && state.stream;
  const busy = isConnecting(state.phase);
  const name = camera.name || camera.deviceId;

  /**
   * Почему не показывает — словами и с тем, что с этим делать. «Не удалось включить
   * камеру» без продолжения — это звонок в поддержку; «Не в сети с 14:32, проверьте
   * питание» — это поход к камере.
   */
  let message: string | null = null;
  let hint: string | null = null;
  if (playable.length === 0) {
    message = t('live.noPlayableProfile');
    hint = t('live.view.hint.noH264');
  } else if (!camera.available) {
    message = t('live.offline');
    hint = t('live.view.hint.unavailable');
  } else if (!live) {
    if (state.phase === 'error' && state.errorKey) {
      message = t(state.errorKey);
      hint =
        state.errorCode === 1001
          ? t('live.view.hint.overloaded')
          : state.errorCode === 480 || camera.online === false
          ? offlineHint()
          : state.errorCode
          ? t('live.code', { code: state.errorCode })
          : t('live.view.hint.retrying');
    } else if (busy) {
      message = t(`live.phase.${state.phase}`);
    } else if (camera.online === false) {
      message = t('live.notConnected');
      hint = offlineHint();
    } else {
      message = t('live.phase.token');
    }
  }
  const failed =
    !live &&
    (state.phase === 'error' || camera.online === false) &&
    playable.length > 0;

  function offlineHint(): string {
    const at = lastSeen ? Date.parse(lastSeen) : NaN;
    return Number.isFinite(at) && at > 0
      ? t('live.view.hint.offlineSince', { value: formatTimestamp(lastSeen!) })
      : t('live.view.hint.offline');
  }

  const v = state.video;
  const facts = [
    v?.width && v?.height ? `${v.width}×${v.height}` : null,
    v?.fps ? t('live.stats.fpsValue', { n: Math.round(v.fps) }) : null,
    state.route === 'relay' ? t('live.route.relay') : null,
  ].filter(Boolean);
  const qualityLabel = t(
    profile === main ? 'live.view.toSub' : 'live.view.toMain',
  );

  return (
    <>
      {live ? <VideoSurface stream={state.stream!} rounded={false} /> : null}
      {message ? (
        <View style={styles.center} pointerEvents="none">
          {busy ? (
            <ActivityIndicator color={ON_VIDEO_MUTED} />
          ) : compact ? null : (
            <Icon
              name={
                playable.length === 0 || !camera.available
                  ? 'warning'
                  : 'camera'
              }
              size={22}
              color={ON_VIDEO_FAINT}
            />
          )}
          <AppText
            variant="micro"
            color={ON_VIDEO_MUTED}
            numberOfLines={compact ? 1 : 2}
            style={styles.centerText}
          >
            {message}
          </AppText>
          {hint && showHint ? (
            <AppText
              variant="micro"
              color={ON_VIDEO_MUTED}
              numberOfLines={3}
              style={styles.centerText}
            >
              {hint}
            </AppText>
          ) : null}
        </View>
      ) : null}
      <View style={styles.head}>
        <View style={styles.nameChip}>
          {/* Зелёная — идёт, янтарная — подключается, серая — нет. Красной нет: красный
              в ui-kit — цвет выбранного, и красные точки на стене читались как тревога. */}
          <View
            style={[
              styles.dot,
              {
                backgroundColor: live
                  ? p.signal
                  : busy
                  ? p.warning
                  : ON_VIDEO_MUTED,
              },
            ]}
          />
          <AppText
            variant="micro"
            color={ON_VIDEO}
            numberOfLines={1}
            style={styles.name}
          >
            {name}
          </AppText>
        </View>
        <View style={styles.headSpacer} />
        {zoom > 1 || controls ? (
          <View style={styles.tools}>
            {zoom > 1 ? (
              <Pressable
                accessibilityRole="button"
                accessibilityLabel={t('live.view.zoomReset')}
                {...tooltip(t('live.view.zoomReset'))}
                onPress={onResetZoom}
                style={styles.chip}
                testID="live-tile-zoom"
              >
                <AppText variant="micro" color={ON_VIDEO}>
                  {`×${zoom.toFixed(1)}`}
                </AppText>
              </Pressable>
            ) : null}
            {controls && playable.length > 1 ? (
              <Pressable
                accessibilityRole="button"
                accessibilityLabel={qualityLabel}
                {...tooltip(qualityLabel)}
                onPress={() => setProfile(cur => (cur === main ? sub : main))}
                style={styles.chip}
                testID="live-tile-quality"
              >
                <AppText variant="micro" color={ON_VIDEO}>
                  {profile === main ? 'HD' : 'SD'}
                </AppText>
              </Pressable>
            ) : null}
            {controls && failed ? (
              <Tool
                icon="rotate"
                label={t('live.view.reconnect')}
                onPress={restart}
                testID="live-tile-reconnect"
              />
            ) : null}
            {controls && live && CAN_SNAPSHOT ? (
              <Tool
                icon="image"
                label={t('live.view.snapshot')}
                onPress={onSnapshot}
                testID="live-tile-snapshot"
              />
            ) : null}
            {controls ? (
              <Tool
                icon="close"
                label={t('live.view.closeWindow')}
                onPress={onClose}
                testID="live-tile-close"
              />
            ) : null}
          </View>
        ) : null}
      </View>
      {hot && live && facts.length > 0 ? (
        <View style={styles.foot} pointerEvents="none">
          <AppText variant="micro" color={ON_VIDEO_MUTED} style={styles.mono}>
            {facts.join(' · ')}
          </AppText>
        </View>
      ) : null}
    </>
  );
}
